import { useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSales } from '../../../../contexts/SalesContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { toast } from 'sonner';

export function useSaleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getSale, updateSale, cancelSale } = useSales();
  const { user } = useAuth();
  const [showPrintOptions, setShowPrintOptions] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const sale = getSale(id);

  const handleBack = () => {
    // Si venimos de la pantalla de caja, volvemos manteniendo el estado
    if (location.state?.returnTo === '/caja') {
      navigate('/caja', {
        state: {
          scrollPosition: location.state.scrollPosition,
          activeTab: location.state.activeTab
        }
      });
    } else {
      navigate('/sales');
    }
  };

  const handlePrint = (type) => {
    console.log(`Imprimiendo comprobante ${type} para venta ${id}`);
    setShowPrintOptions(false);
  };

  const handleEdit = () => {
    console.log(`Editando venta ${id}`);
    setShowPrintOptions(false);
  };

  const handleReturn = () => {
    console.log(`Registrando devolución para venta ${id}`);
    setShowPrintOptions(false);
  };

  const handleDelete = async () => {
    try {
      // Mostrar un diálogo de confirmación
      const confirmed = window.confirm(
        `¿Estás seguro de que deseas cancelar la venta #${sale.display_id}? Esta acción devolverá los productos al inventario y no se puede deshacer.`
      );
      
      if (!confirmed) return;
      
      // Solicitar el motivo de la cancelación
      const reason = prompt('Por favor, ingresa el motivo de la cancelación:');
      if (!reason) return; // Si el usuario cancela o no ingresa un motivo
      
      // Llamar a la función cancelSale con el motivo
      await cancelSale(id, { reason });
      
      // Opcional: redirigir a la lista de ventas o mostrar un mensaje de éxito
      alert('La venta ha sido cancelada exitosamente.');
    } catch (error) {
      console.error('Error al cancelar la venta:', error);
      alert(`Error al cancelar la venta: ${error.message}`);
    }
  };

  const handleDeliveryClick = () => {
    setShowDeliveryModal(true);
  };

  const handleDeliveryConfirm = async (deliveryInfo) => {
    try {
      // Determinar si la venta debería marcarse como completada
      const shouldComplete = sale.payment_status === 'paid';

      await updateSale(id, {
        ...sale,
        status: shouldComplete ? 'completed' : sale.status,
        delivery_status: 'delivered',
        delivered_by: deliveryInfo.staffName,
        delivered_at: deliveryInfo.timestamp,
        prepared_items: deliveryInfo.preparedItems
      });
      setShowDeliveryModal(false);
    } catch (error) {
      console.error('Error al confirmar entrega:', error);
    }
  };

  const handlePaymentClick = () => {
    setShowPaymentModal(true);
  };

  const handlePaymentConfirm = async (paymentInfo) => {
    try {
      // Determinar si la venta debería marcarse como completada
      const shouldComplete = sale.delivery_status === 'delivered';
      
      await updateSale(id, {
        ...sale,
        status: shouldComplete ? 'completed' : 'paid',
        payment_status: 'paid',
        payments: paymentInfo.payments,
        cashier: paymentInfo.cashier,
        paid_at: new Date().toISOString(),
        paid_by: user?.id || null
      });
      
      // Mostrar notificación de éxito
      toast.success('Pago registrado con éxito');
      
      setShowPaymentModal(false);
    } catch (error) {
      console.error('Error al procesar el pago:', error);
      toast.error('Error al procesar el pago');
    }
  };

  return {
    sale,
    showPrintOptions,
    setShowPrintOptions,
    showDeliveryModal,
    setShowDeliveryModal,
    showPaymentModal,
    setShowPaymentModal,
    handleBack,
    handlePrint,
    handleEdit,
    handleReturn,
    handleDelete,
    handleDeliveryClick,
    handleDeliveryConfirm,
    handlePaymentClick,
    handlePaymentConfirm
  };
} 